import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, TrendingUp, MessageSquare, Target, Sparkles, Copy, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Layout } from "../components/Layout";
import { AnimatedNumber } from "../components/ScoreRing";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { NOVA_DEMO } from "../lib/demoData";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";

function Metric({ icon: Icon, value, label, sub, color, decimals = 0, testid }) {
  return (
    <div className="lb-card lb-card-hover p-5" data-testid={testid}>
      <div className={`flex items-center gap-2 mb-2 ${color}`}><Icon size={18} /><span className="font-display font-black text-2xl"><AnimatedNumber value={value} decimals={decimals} /></span></div>
      <p className="text-xs text-[hsl(var(--muted-foreground))]">{label}</p>
      {sub && <p className="text-xs text-emerald-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function Reputation() {
  const { data, demo, setData } = useApp();
  const { t } = useLang();
  const rep = data?.reputation || (demo ? NOVA_DEMO.reputation : null);
  const [campaign, setCampaign] = useState("");
  const [reply, setReply] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingReply, setLoadingReply] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    average: rep?.average ?? 4.5, count: rep?.count ?? 0, evolution: rep?.evolution ?? 0,
    monthly_goal: rep?.monthly_goal ?? 20, gained: rep?.gained ?? 0,
    reviews: rep?.reviews ? [...rep.reviews] : [{ name: "", rating: 5, text: "" }],
  });
  const biz = data?.onboarding?.business_name || "mon commerce";

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setReview = (i, k, v) => setForm((p) => ({ ...p, reviews: p.reviews.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addReview = () => setForm((p) => ({ ...p, reviews: [...p.reviews, { name: "", rating: 5, text: "" }] }));
  const delReview = (i) => setForm((p) => ({ ...p, reviews: p.reviews.filter((_, idx) => idx !== i) }));

  const saveRep = () => {
    const cleanReviews = form.reviews.filter((r) => r.text || r.name).map((r) => ({ name: r.name || "Client", rating: Number(r.rating) || 5, text: r.text || "" }));
    const newRep = {
      average: Math.max(0, Math.min(5, Number(form.average) || 0)),
      count: Math.max(0, parseInt(form.count) || 0),
      evolution: Number(form.evolution) || 0,
      monthly_goal: Math.max(0, parseInt(form.monthly_goal) || 0),
      gained: Math.max(0, parseInt(form.gained) || 0),
      reviews: cleanReviews,
    };
    setData((d) => ({ ...(d || {}), reputation: newRep }));
    setEditOpen(false);
    toast.success("Données de réputation enregistrées !");
  };

  const genCampaign = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.post("/ai/content", { content_type: "whatsapp", tone: "chaleureux", topic: "demander gentiment un avis Google après une visite", business_name: biz, action: "generate" });
      setCampaign(res.content);
      toast.success("Campagne d'avis générée !");
    } catch { toast.error("Erreur"); } finally { setLoading(false); }
  };

  const genReply = async (i, review) => {
    setLoadingReply(i);
    try {
      const { data: res } = await api.post("/ai/content", { content_type: "email", tone: "professionnel", topic: `réponse courte et sincère à cet avis client (${review.rating}/5): "${review.text}"`, business_name: biz, action: "generate" });
      setReply((r) => ({ ...r, [i]: res.content }));
    } catch { toast.error("Erreur"); } finally { setLoadingReply(null); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div><h1 className="font-display font-black text-2xl lg:text-3xl">{t("title_reputation")}</h1><p className="text-sm text-[hsl(var(--muted-foreground))]">Collectez plus d'avis et répondez mieux.</p></div>
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogTrigger asChild>
            <Button data-testid="rep-edit-btn" variant="outline" className="rounded-full border-white/10 bg-white/5 h-10 text-xs"><Pencil size={14} className="mr-1.5" /> Saisir mes données</Button>
          </DialogTrigger>
          <DialogContent className="glass border-white/10 max-h-[85vh] overflow-y-auto" data-testid="rep-edit-dialog">
            <DialogHeader>
              <DialogTitle>Mes données de réputation</DialogTitle>
              <DialogDescription className="text-xs">Saisissez vos vrais chiffres Google / avis clients.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Note moyenne (/5)</Label><Input data-testid="rep-in-average" type="number" step="0.1" min="0" max="5" value={form.average} onChange={(e) => setF("average", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-10" /></div>
              <div><Label className="text-xs">Nombre d'avis</Label><Input data-testid="rep-in-count" type="number" min="0" value={form.count} onChange={(e) => setF("count", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-10" /></div>
              <div><Label className="text-xs">Évolution ce mois</Label><Input data-testid="rep-in-evolution" type="number" step="0.1" value={form.evolution} onChange={(e) => setF("evolution", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-10" /></div>
              <div><Label className="text-xs">Objectif mensuel</Label><Input data-testid="rep-in-goal" type="number" min="0" value={form.monthly_goal} onChange={(e) => setF("monthly_goal", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-10" /></div>
            </div>
            <div className="mt-2">
              <div className="flex items-center justify-between mb-2"><Label className="text-xs">Avis clients</Label><button onClick={addReview} data-testid="rep-add-review" className="text-xs text-neon flex items-center gap-1"><Plus size={12} /> Ajouter</button></div>
              <div className="space-y-2">
                {form.reviews.map((r, i) => (
                  <div key={i} className="rounded-xl bg-white/[0.02] border border-white/5 p-2.5 space-y-2" data-testid={`rep-review-row-${i}`}>
                    <div className="flex gap-2">
                      <Input data-testid={`rep-review-name-${i}`} placeholder="Nom" value={r.name} onChange={(e) => setReview(i, "name", e.target.value)} className="bg-white/5 border-white/10 h-9 text-xs" />
                      <Input data-testid={`rep-review-rating-${i}`} type="number" min="1" max="5" value={r.rating} onChange={(e) => setReview(i, "rating", e.target.value)} className="bg-white/5 border-white/10 h-9 text-xs w-20" />
                      <button onClick={() => delReview(i)} className="text-[hsl(var(--muted-foreground))] hover:text-red-400 shrink-0"><Trash2 size={15} /></button>
                    </div>
                    <Input data-testid={`rep-review-text-${i}`} placeholder="Texte de l'avis" value={r.text} onChange={(e) => setReview(i, "text", e.target.value)} className="bg-white/5 border-white/10 h-9 text-xs" />
                  </div>
                ))}
              </div>
            </div>
            <Button data-testid="rep-save" onClick={saveRep} className="w-full rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn mt-2">Enregistrer</Button>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {rep ? (
          <>
            <Metric icon={Star} value={rep.average} decimals={1} label="Note moyenne" color="text-yellow-400" testid="rep-average" />
            <Metric icon={TrendingUp} value={rep.evolution} decimals={1} label="Évolution ce mois" sub="En hausse" color="text-emerald-400" testid="rep-evolution" />
            <Metric icon={MessageSquare} value={rep.count} label="Nombre d'avis" color="text-neon" testid="rep-count" />
            <Metric icon={Target} value={rep.gained} label={`Objectif : ${rep.monthly_goal}/mois`} color="text-primary-foreground" testid="rep-goal" />
          </>
        ) : (
          [["rep-average", Star, "Note moyenne"], ["rep-evolution", TrendingUp, "Évolution ce mois"], ["rep-count", MessageSquare, "Nombre d'avis"], ["rep-goal", Target, "Objectif mensuel"]].map(([tid, Icon, label]) => (
            <div key={tid} className="lb-card p-5" data-testid={tid}>
              <div className="flex items-center gap-2 mb-2 text-[hsl(var(--muted-foreground))]"><Icon size={18} /><span className="font-display font-black text-2xl">—</span></div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{label}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Non renseigné</p>
            </div>
          ))
        )}
      </div>
      {!rep && (
        <div className="lb-card p-4 mb-4 text-sm text-[hsl(var(--muted-foreground))]" data-testid="rep-notice">
          Connectez vos avis ou saisissez vos données pour suivre votre réputation. Vous pouvez déjà préparer une campagne de demande d'avis ci-dessous.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="lb-card p-5" data-testid="rep-campaign">
          <h3 className="font-display font-bold text-lg mb-1">Campagne de demande d'avis</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">Générez un message à envoyer à vos clients.</p>
          <Button data-testid="rep-gen-campaign" onClick={genCampaign} disabled={loading} className="rounded-full bg-gradient-to-r from-primary to-neon text-white lb-glow-btn mb-4 h-10">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <><Sparkles size={15} className="mr-1" /> Générer la campagne</>}
          </Button>
          {campaign && (
            <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4 text-sm whitespace-pre-wrap relative">
              {campaign}
              <button onClick={async () => { try { await navigator.clipboard.writeText(campaign); toast.success("Copié !"); } catch { toast.error("Impossible de copier."); } }} className="absolute top-2 right-2 text-[hsl(var(--muted-foreground))] hover:text-white"><Copy size={14} /></button>
            </div>
          )}
        </div>

        <div className="lb-card p-5" data-testid="rep-reviews">
          <h3 className="font-display font-bold text-lg mb-4">Avis récents & réponses suggérées</h3>
          <div className="space-y-3">
            {!rep && <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="rep-reviews-empty">Aucun avis à afficher pour le moment. Non renseigné.</p>}
            {(rep?.reviews || []).map((rv, i) => (
              <div key={i} className="rounded-xl bg-white/[0.02] border border-white/5 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{rv.name}</span>
                  <span className="flex gap-0.5">{[...Array(5)].map((_, s) => <Star key={s} size={12} className={s < rv.rating ? "text-yellow-400" : "text-white/15"} fill="currentColor" />)}</span>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">{rv.text}</p>
                {reply[i] ? (
                  <div className="text-xs bg-primary/10 border border-primary/20 rounded-lg p-2 mt-2 whitespace-pre-wrap">{reply[i]}</div>
                ) : (
                  <Button data-testid={`rep-reply-${i}`} onClick={() => genReply(i, rv)} disabled={loadingReply === i} variant="outline" size="sm" className="rounded-full text-xs h-7 border-white/10 bg-white/5">
                    {loadingReply === i ? <Loader2 size={12} className="animate-spin" /> : "Suggérer une réponse"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
